'use client';

import { c as _c } from "react/compiler-runtime";
import { useVoteOnOutcomeSuggestion } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
interface DuplicateErrorBannerProps {
  conflict: {
    kind: 'suggestion' | 'onchain_outcome';
    id: string;
    label: string;
    upvotes: number;
    downvotes: number;
  };
  onDismiss: () => void;
}

/**
 * Inline 422 error banner shown in the suggest-outcome wizard when the
 * normalised label collides with an existing on-chain outcome OR an
 * active suggestion. Lets the user one-tap upvote the existing one
 * instead of resubmitting.
 */
export function DuplicateErrorBanner(t0) {
  const $ = _c(32);
  const {
    conflict,
    onDismiss
  } = t0;
  const t = useTranslations("launchpad.suggestedOutcomes.duplicate");
  const voteMutation = useVoteOnOutcomeSuggestion();
  const canUpvote = conflict.kind === "suggestion" && conflict.id.length > 0;
  let t1;
  if ($[0] !== canUpvote || $[1] !== conflict.id || $[2] !== onDismiss || $[3] !== voteMutation) {
    t1 = async function handleUpvote() {
      if (!canUpvote) {
        return;
      }
      await voteMutation.mutateAsync({
        id: conflict.id,
        kind: "up"
      });
      onDismiss();
    };
    $[0] = canUpvote;
    $[1] = conflict.id;
    $[2] = onDismiss;
    $[3] = voteMutation;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleUpvote = t1;
  let t2;
  if ($[5] !== t) {
    t2 = t("title");
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <p className="text-caption font-medium text-feedback-error-content">{t2}</p>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== conflict.label || $[10] !== t) {
    t4 = t("body", {
      label: conflict.label
    });
    $[9] = conflict.label;
    $[10] = t;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t4) {
    t5 = <p className="mt-1 text-caption text-foreground-primary">{t4}</p>;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== canUpvote || $[15] !== conflict.upvotes || $[16] !== handleUpvote || $[17] !== t || $[18] !== voteMutation) {
    t6 = canUpvote ? <AnimatedButton variant="primary" size="sm" onClick={handleUpvote} disabled={voteMutation.isPending} loading={voteMutation.isPending}>{t("upvoteExisting", {
        upvotes: conflict.upvotes
      })}</AnimatedButton> : null;
    $[14] = canUpvote;
    $[15] = conflict.upvotes;
    $[16] = handleUpvote;
    $[17] = t;
    $[18] = voteMutation;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t) {
    t7 = t("dismiss");
    $[20] = t;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== onDismiss || $[23] !== t7) {
    t8 = <AnimatedButton variant="tertiary" size="sm" onClick={onDismiss}>{t7}</AnimatedButton>;
    $[22] = onDismiss;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t6 || $[26] !== t8) {
    t9 = <div className="mt-2 flex gap-2">{t6}{t8}</div>;
    $[25] = t6;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t3 || $[29] !== t5 || $[30] !== t9) {
    t10 = <div className="rounded-panel-inset border border-feedback-error-border bg-feedback-error-surface px-4 py-3">{t3}{t5}{t9}</div>;
    $[28] = t3;
    $[29] = t5;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}